// SPDX-License-Identifier: LicenseRef-Proprietary

import { useEffect, useCallback } from 'react';
import type { AnalysisProgress } from './useAnalysisProgress';
import type { AbstractTriageBrief } from '../../../lib/models';

interface NavigationState {
  // Analysis state
  triageBriefs: Record<string, AbstractTriageBrief>;
  triageErrors: Record<string, { code: string; message: string }>;
  triageStatus: 'idle' | 'loading' | 'cancelled' | 'done' | 'error';
  failedPmids: string[];
  selectedPmids: string[];
  analysisProgress: AnalysisProgress | null;
  selectedPmid: string | null;
  
  // UI state
  activeTab: 'search' | 'library' | 'scenario';
  selectedInputSource: string | null;
  searchQuery: string;
  isOpen: boolean;
  isMaximized: boolean;
  
  // Timestamps for state validation
  lastUpdated: number;
  sessionId: string;
}

interface NavigationStatePreservationHook {
  preserveState: (state: Partial<NavigationState>) => void;
  restoreState: () => NavigationState | null;
  clearState: () => void;
  isStateValid: (state: NavigationState | null) => boolean;
}

const STORAGE_KEY = 'evidenceDrawer.navigationState';
const STATE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes
const SESSION_ID = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const useNavigationStatePreservation = (): NavigationStatePreservationHook => {
  const preserveState = useCallback((state: Partial<NavigationState>) => {
    try {
      const stateToSave: NavigationState = {
        triageBriefs: {},
        triageErrors: {},
        triageStatus: 'idle',
        failedPmids: [],
        selectedPmids: [],
        analysisProgress: null,
        selectedPmid: null,
        activeTab: 'search',
        selectedInputSource: null,
        searchQuery: '',
        isOpen: false,
        isMaximized: false,
        lastUpdated: Date.now(),
        sessionId: SESSION_ID,
        ...state,
      };

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.warn('Failed to preserve navigation state:', error);
    }
  }, []);

  const clearState = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear navigation state:', error);
    }
  }, []);

  const isStateValid = useCallback((state: NavigationState | null): boolean => {
    if (!state) return false;
    
    // Check if state is expired
    const isExpired = Date.now() - state.lastUpdated > STATE_EXPIRY_MS;
    if (isExpired) return false;
    
    // Check if state is from same session (prevents cross-tab conflicts)
    const isSameSession = state.sessionId === SESSION_ID;
    if (!isSameSession) return false;
    
    return true;
  }, []);

  const restoreState = useCallback((): NavigationState | null => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (!saved) return null;

      const state: NavigationState = JSON.parse(saved);
      
      // Validate state is not expired and from same session
      if (!isStateValid(state)) {
        clearState();
        return null;
      }

      return state;
    } catch (error) {
      console.warn('Failed to restore navigation state:', error);
      clearState();
      return null;
    }
  }, [clearState, isStateValid]);

  // Clear state on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only clear if analysis is not in progress
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const state: NavigationState = JSON.parse(saved);
          if (state.triageStatus !== 'loading') {
            clearState();
          }
        } catch {
          clearState();
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [clearState]);

  return {
    preserveState,
    restoreState,
    clearState,
    isStateValid,
  };
};
